import React, { useEffect, useState } from 'react'
import {
  createFeatureRequest, getConvoSummary, getFeatureRequests, getWaGroups, linkWaGroup, updateFeatureRequest,
  type ConvoSummary, type FeatureRequest, type WaGroup,
} from './crmApi'
import { crmToast, SearchInput } from './crmUi'
import { SkeletonRows } from '../shared/Skeleton'

/**
 * Conversation History (Lee, 3 Aug 2026) — three tabs on every lead:
 *   Support: Claude summary of tickets + support chats, platform-only bullets,
 *            latest first. WhatsApp: same over the LINKED WhatsApp group's last two
 *            months (link the group first). Feature Requests: the per-lead ledger —
 *            added here manually now; the Support side forwards into the same list
 *            later (source chips already distinguish them).
 */

const SOURCE_STYLE: Record<string, { bg: string; fg: string }> = {
  manual: { bg: '#F1F5F9', fg: '#334155' },
  support: { bg: '#DBEAFE', fg: '#1E40AF' },
  whatsapp: { bg: '#DCFCE7', fg: '#166534' },
}
const FR_STATUSES = ['open', 'planned', 'done', 'rejected'] as const

const Bullets: React.FC<{ text: string }> = ({ text }) => (
  <div className="space-y-1.5">
    {text.split('\n').filter((l) => l.trim()).map((l, i) => (
      <p key={i} className="text-[13px] text-txt-primary leading-relaxed pl-4 -indent-4">
        {l.trim().startsWith('-') ? l.trim() : `- ${l.trim()}`}
      </p>
    ))}
  </div>
)

const SummaryPane: React.FC<{ dealId: string; kind: 'support' | 'whatsapp' }> = ({ dealId, kind }) => {
  const [data, setData] = useState<ConvoSummary | null>(null)
  const [busy, setBusy] = useState(false)
  const [groups, setGroups] = useState<WaGroup[] | null>(null)
  const [gatewayUp, setGatewayUp] = useState(true)
  const [search, setSearch] = useState('')

  const load = (regen = false) => {
    setBusy(true)
    getConvoSummary(dealId, kind, regen)
      .then(setData)
      .catch(() => crmToast('Could not load the summary', 'error'))
      .finally(() => setBusy(false))
  }
  useEffect(() => { load(false) }, [dealId, kind]) // eslint-disable-line react-hooks/exhaustive-deps

  // WhatsApp link picker — LIVE groups from the running gateway.
  useEffect(() => {
    if (kind !== 'whatsapp' || data?.linked !== false) return
    getWaGroups(search || undefined)
      .then((r) => { setGroups(r.groups); setGatewayUp(r.available) })
      .catch(() => { setGroups([]); setGatewayUp(false) })
  }, [kind, data?.linked, search])

  if (!data) return <SkeletonRows rows={4} rowClass="h-12" />

  if (kind === 'whatsapp' && !data.linked) {
    return (
      <div className="max-w-[560px]">
        <p className="text-sm text-txt-secondary mb-1">Link this lead’s WhatsApp group first — these are the LIVE groups on the connected WhatsApp number; the summary reads the group’s conversation (last 2 months).</p>
        <SearchInput value={search} onChange={setSearch} placeholder="Search live WhatsApp groups…" className="my-2" />
        <div className="rounded-xl border border-border divide-y divide-border max-h-72 overflow-y-auto">
          {groups === null ? (
            <p className="text-[12px] text-txt-tertiary p-3">Loading live groups…</p>
          ) : !gatewayUp ? (
            <p className="text-[12px] text-amber-700 p-3">WhatsApp gateway not reachable — check the connected number in Settings → WhatsApp.</p>
          ) : groups.length === 0 ? (
            <p className="text-[12px] text-txt-tertiary p-3">No live groups match.</p>
          ) : (
            groups.map((g) => (
              <button
                key={g.jid}
                onClick={() =>
                  linkWaGroup(dealId, g.jid, g.name)
                    .then(() => { crmToast(`Linked “${g.name}” — generating the summary…`, 'success'); load(true) })
                    .catch(() => crmToast('Could not link the group', 'error'))
                }
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-surface-hover"
              >
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-txt-primary truncate">{g.name}</span>
                  <span className="block text-[11px] text-txt-tertiary truncate">{g.jid}</span>
                </span>
                <span className="text-[11px] font-bold text-brand-600 shrink-0">Link →</span>
              </button>
            ))
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-[720px]">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <p className="text-[11px] text-txt-tertiary">
          {kind === 'whatsapp' && data.groupName ? <>Group: <span className="font-semibold text-txt-secondary">{data.groupName}</span> · </> : null}
          {data.itemCount} item{data.itemCount === 1 ? '' : 's'}
          {data.generatedAt ? ` · summarised ${new Date(data.generatedAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
          {' · platform-related info only, latest first'}
        </p>
        <div className="flex items-center gap-2">
          {kind === 'whatsapp' && (
            <button
              onClick={() => linkWaGroup(dealId, null, null).then(() => { crmToast('Group unlinked', 'success'); load(false) })}
              className="px-2.5 py-1.5 rounded-lg text-[11px] text-txt-tertiary border border-border hover:text-txt-primary"
            >
              Change group
            </button>
          )}
          <button
            onClick={() => load(true)}
            disabled={busy}
            className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border border-border text-txt-primary hover:bg-surface-hover disabled:opacity-50"
          >
            {busy ? 'Summarising…' : 'Regenerate'}
          </button>
        </div>
      </div>
      <div className="rounded-xl border border-border bg-surface p-4">
        {busy && !data.summary ? <p className="text-sm text-txt-tertiary">Summarising…</p> : <Bullets text={data.summary || '- Nothing yet.'} />}
      </div>
    </div>
  )
}

const FeatureRequestsPane: React.FC<{ dealId: string }> = ({ dealId }) => {
  const [items, setItems] = useState<FeatureRequest[] | null>(null)
  const [title, setTitle] = useState('')
  const [detail, setDetail] = useState('')

  const load = () => getFeatureRequests(dealId).then(setItems).catch(() => setItems([]))
  useEffect(() => { load() }, [dealId]) // eslint-disable-line react-hooks/exhaustive-deps

  const add = () => {
    if (title.trim().length < 2) { crmToast('Give the request a title', 'error'); return }
    createFeatureRequest(dealId, { title: title.trim(), detail: detail.trim() || null })
      .then(() => { setTitle(''); setDetail(''); crmToast('Feature request added', 'success'); load() })
      .catch(() => crmToast('Could not add the request', 'error'))
  }

  return (
    <div className="max-w-[720px] space-y-4">
      <div className="rounded-xl border border-border bg-surface p-3 space-y-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary">Add a feature request</p>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What do they want?" className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:border-brand-400" />
        <textarea value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Detail / context (optional)" rows={2} className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-txt-primary placeholder:text-txt-tertiary focus:outline-none focus:border-brand-400 resize-none" />
        <div className="flex justify-end"><button onClick={add} className="px-3.5 py-1.5 rounded-lg text-xs font-bold text-white bg-brand-600 hover:bg-brand-700">Add request</button></div>
      </div>

      {items === null ? (
        <p className="text-sm text-txt-tertiary">Loading…</p>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-6 text-center">
          <p className="text-sm text-txt-secondary">No feature requests yet.</p>
          <p className="text-[11px] text-txt-tertiary mt-1">Add them here, or they arrive forwarded from Support and WhatsApp.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => {
            const s = SOURCE_STYLE[r.source] || SOURCE_STYLE.manual
            return (
              <div key={r.id} className="rounded-xl border border-border bg-surface px-3.5 py-2.5">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0">
                    <p className={`text-[13px] font-semibold ${r.status === 'done' ? 'text-txt-tertiary line-through' : 'text-txt-primary'}`}>{r.title}</p>
                    {r.detail && <p className="text-[12px] text-txt-secondary whitespace-pre-wrap mt-0.5">{r.detail}</p>}
                    <p className="text-[10px] text-txt-tertiary mt-1">
                      <span className="px-1.5 py-0.5 rounded font-bold uppercase mr-1" style={{ background: s.bg, color: s.fg }}>{r.source}</span>
                      {r.createdAt ? new Date(r.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''}{r.createdBy ? ` · ${r.createdBy}` : ''}
                    </p>
                  </div>
                  <select
                    value={r.status}
                    onChange={(e) => updateFeatureRequest(r.id, { status: e.target.value }).then(load).catch(() => crmToast('Could not update', 'error'))}
                    className="rounded-lg border border-border bg-surface px-2 py-1 text-[11px] font-semibold text-txt-primary focus:outline-none"
                  >
                    {FR_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export const DealConvoHistory: React.FC<{ dealId: string }> = ({ dealId }) => {
  const [tab, setTab] = useState<'support' | 'whatsapp' | 'requests'>('support')
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-4">
        {([['support', 'Support'], ['whatsapp', 'WhatsApp'], ['requests', 'Feature Requests']] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${tab === k ? 'bg-brand-600 border-brand-600 text-white' : 'border-border text-txt-secondary hover:bg-surface-hover'}`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === 'support' && <SummaryPane dealId={dealId} kind="support" />}
      {tab === 'whatsapp' && <SummaryPane dealId={dealId} kind="whatsapp" />}
      {tab === 'requests' && <FeatureRequestsPane dealId={dealId} />}
    </div>
  )
}
